import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { FlagsService } from './flags.service';

const DEFAULT_TOUCH_DEVICE_MAX_CONCURRENT_LOADS = 1;
const DEFAULT_DESKTOP_MAX_CONCURRENT_LOADS = 10;

@Injectable()
export class ImageLoadingQueueService {
  // Limit how many images can load at the same time.
  private readonly maxConcurrentLoads: number;

  private currentActiveLoads = 0;
  private readonly pendingQueue: Array<() => void> = [];

  constructor(
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private flagsService: FlagsService,
  ) {
    const touchDevicesMaxConcurrentLoads = Number(
      this.flagsService.featureFlagsVariables.enable_image_loading_queue
        ?.touch_device_max_concurrent_loads ?? DEFAULT_TOUCH_DEVICE_MAX_CONCURRENT_LOADS,
    );
    const desktopMaxConcurrentLoads = Number(
      this.flagsService.featureFlagsVariables.enable_image_loading_queue
        ?.desktop_max_concurrent_loads ?? DEFAULT_DESKTOP_MAX_CONCURRENT_LOADS,
    );
    this.maxConcurrentLoads = this.deviceAndBrowserDetectorService.isTouchDevice()
      ? touchDevicesMaxConcurrentLoads
      : desktopMaxConcurrentLoads;
  }

  /**
   * Request a loading slot. The observable completes once the caller
   * is allowed to start loading the image.
   */
  requestSlot(): Observable<void> {
    return new Observable<void>((subscriber) => {
      const tryAcquire = () => {
        if (subscriber.closed) {
          return;
        }

        if (this.currentActiveLoads < this.maxConcurrentLoads) {
          this.currentActiveLoads++;
          subscriber.next();
          subscriber.complete();
          return;
        }

        this.pendingQueue.push(tryAcquire);
      };

      tryAcquire();

      return () => {
        const index = this.pendingQueue.indexOf(tryAcquire);
        if (index > -1) {
          this.pendingQueue.splice(index, 1);
        }
      };
    });
  }

  /**
   * Release a previously acquired slot and wake the next waiting request (if any).
   */
  releaseSlot(): void {
    if (this.currentActiveLoads > 0) {
      this.currentActiveLoads--;
    }

    const next = this.pendingQueue.shift();
    if (next) {
      next();
    }
  }
}
